import { useMemo } from 'react';
import cn from 'classnames';
import { Checkbox, Collapse, Input, Select } from 'antd';
import { ControlOutlined, SearchOutlined } from '@ant-design/icons';
import type {
  DoctorInfo,
  SpecializationInfo,
} from '../../../../models/Settings/ClinicSettings';
import styles from '../styles.module.css';

export interface ScenarioParticipationProps {
  callsDisable: boolean | null | undefined;
  excludedDoctorIds: string[];
  onToggleSpecialization: (id: string) => void;
  onToggleDoctor: (id: string) => void;
  doctorSearch: string;
  onDoctorSearchChange: (v: string) => void;
  specFilter: string | null;
  onSpecFilterChange: (v: string | null) => void;
  specializations: SpecializationInfo[];
  doctors: DoctorInfo[];
  disabled?: boolean;
}

export const ScenarioParticipation = ({
  callsDisable,
  excludedDoctorIds,
  onToggleSpecialization,
  onToggleDoctor,
  doctorSearch,
  onDoctorSearchChange,
  specFilter,
  onSpecFilterChange,
  specializations,
  doctors,
  disabled = false,
}: ScenarioParticipationProps) => {
  const isCallsDisabled = callsDisable === true;
  // Group doctors by specialization — build a map specId → doctors
  const specCollapseItems = useMemo(() => {
    // Filter specializations by specFilter
    const filteredSpecs = specFilter
      ? specializations.filter((s) => s.id === specFilter)
      : specializations;

    return filteredSpecs
      .map((spec) => {
        // Doctors belonging to this specialization
        const specDoctors = doctors.filter(
          (doc) =>
            doc.specializations?.some((ds) => ds.id === spec.id) ?? false,
        );

        // Не показываем специализации без врачей
        if (specDoctors.length === 0) return null;

        // Compute tri-state checkbox for specialization
        const totalDoctors = specDoctors.length;
        const excludedCount = specDoctors.filter((d) =>
          excludedDoctorIds.includes(d.id),
        ).length;

        const allExcluded = totalDoctors > 0 && excludedCount === totalDoctors;
        const noneExcluded = excludedCount === 0;
        const isIndeterminate =
          totalDoctors > 0 && !allExcluded && !noneExcluded;
        const isChecked = !allExcluded && totalDoctors > 0;

        return {
          key: spec.id,
          label: (
            <div className={styles.specHeader}>
              <Checkbox
                checked={isChecked}
                indeterminate={isIndeterminate}
                disabled={disabled || isCallsDisabled}
                onClick={(e) => e.stopPropagation()}
                onChange={() => onToggleSpecialization(spec.id)}
              />
              <span>{spec.title || 'Специализация не указана'}</span>
            </div>
          ),
          children: (
            <div className={styles.doctorList}>
              {specDoctors.map((doc) => {
                const isDocExcluded = excludedDoctorIds.includes(doc.id);
                const fullName = [doc.surname, doc.givenName, doc.patronym]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <div key={doc.id} className={styles.doctorItem}>
                    <Checkbox
                      checked={!isDocExcluded}
                      disabled={disabled || isCallsDisabled}
                      onChange={() => onToggleDoctor(doc.id)}
                    />
                    <span>{fullName || '—'}</span>
                  </div>
                );
              })}
            </div>
          ),
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [
    specializations,
    doctors,
    excludedDoctorIds,
    specFilter,
    isCallsDisabled,
    disabled,
    onToggleSpecialization,
    onToggleDoctor,
  ]);

  return (
    <div
      className={cn(styles.scenarioParamsAnimWrap, styles.scenarioParamsOpen)}
    >
      <div className={styles.scenarioParamsAnimInner}>
        <div className={styles.separator} />

        {/* Параметры сценария */}
        <div className={cn(styles.scenarioParamsHeader, styles.scenarioPadded)}>
          <label className={styles.fieldHeader}>
            <ControlOutlined className={styles.fieldIcon} />
            Параметры сценария
          </label>
          <div className={styles.scenarioFilters}>
            <Input
              placeholder="Поиск по ФИО врача"
              prefix={<SearchOutlined />}
              value={doctorSearch}
              onChange={(e) => onDoctorSearchChange(e.target.value)}
              allowClear
              className={styles.scenarioSearchInput}
              disabled={disabled}
            />
            <Select
              className={styles.scenarioSpecSelect}
              placeholder="Все специализации"
              value={specFilter}
              onChange={onSpecFilterChange}
              allowClear
              options={specializations.map((s) => ({
                value: s.id,
                label: s.title || 'Специализация не указана',
              }))}
              disabled={disabled}
            />
          </div>
        </div>

        {/* Clinic participation settings */}
        <div className={cn(styles.clinicExpansionWrap)}>
          <Collapse
            defaultActiveKey={['clinic']}
            className={styles.clinicCollapse}
            expandIconPosition="end"
            bordered={false}
            items={[
              {
                key: 'clinic',
                label: (
                  <div className={styles.clinicCollapseHeader}>
                    <span className={styles.clinicCollapseName}>
                      Настройка участия в обзвоне:
                    </span>
                  </div>
                ),
                children: (
                  <div>
                    {specCollapseItems.length > 0 ? (
                      <Collapse
                        className={styles.specCollapse}
                        expandIconPosition="end"
                        items={specCollapseItems}
                      />
                    ) : (
                      <span className={styles.emptyText}>
                        Нет доступных специализаций
                      </span>
                    )}
                  </div>
                ),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};
